import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ShoppingCart, 
    Menu, 
    X, 
    Search,
    Truck,
    Phone,
    ChevronRight,
    Home,
    Package,
    Banknote,
    Shield,
    Star,
    MapPin
} from 'lucide-react';
import { useCartStore, useCartTotals } from '../../store/cartStore';
import { cn, formatPrice } from '../../lib/utils';

export function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const location = useLocation();
    const navigate = useNavigate();

    const openCart = useCartStore((state) => state.openCart);
    const items = useCartStore((state) => state.items);
    const { totalItems, totalPrice } = useCartTotals();

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
    }, [location.pathname]);

    // Focus search input when opened
    useEffect(() => {
        if (isSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchOpen]);

    // Handle search submit
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
            setIsSearchOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <>
            {/* Top Bar - Desktop Only */}
            <div className="hidden md:block bg-gray-900 text-white text-sm py-2">
                <div className="container-custom flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <span className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-primary-400" />
                            <span>Livraison partout en Algérie</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <Banknote className="w-4 h-4 text-green-400" />
                            <span>Paiement à la livraison</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <span className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-primary-400" />
                            <span>Support: 0550 00 00 00</span>
                        </span>
                        <span className="flex items-center gap-1 text-amber-400">
                            <Star className="w-4 h-4 fill-amber-400" />
                            <span>4.8/5 (2000+ avis)</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <header
                className={cn(
                    'sticky top-0 left-0 right-0 z-50 transition-all duration-300',
                    isScrolled
                        ? 'bg-white shadow-lg'
                        : 'bg-white/95 backdrop-blur-md'
                )}
            >
                <div className="container-custom py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
                            <img
                                src="/logo.png"
                                alt="Marchego"
                                className="h-9 md:h-11 w-auto"
                            />
                        </Link>

                        {/* Search Bar - Desktop */}
                        <div className="hidden md:flex flex-1 max-w-xl mx-8">
                            <form onSubmit={handleSearch} className="w-full relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher un produit..."
                                    className="w-full pl-12 pr-4 py-3 bg-gray-100 border-2 border-transparent rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-primary-500 transition-all"
                                />
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </form>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                            {/* Search Button - Mobile */}
                            <button
                                onClick={() => setIsSearchOpen(!isSearchOpen)}
                                className="md:hidden p-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                                aria-label="Search"
                            >
                                <Search className="w-5 h-5" />
                            </button>

                            {/* Cart Button */}
                            <button
                                onClick={openCart}
                                className="relative flex items-center gap-2 p-2.5 md:px-4 md:py-2.5 rounded-xl bg-primary-50 hover:bg-primary-100 text-primary-600 transition-all group"
                                aria-label="Open cart"
                            >
                                <div className="relative">
                                    <ShoppingCart className="w-5 h-5" />
                                    <AnimatePresence>
                                        {totalItems > 0 && (
                                            <motion.span
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                exit={{ scale: 0 }}
                                                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full"
                                            >
                                                {totalItems > 99 ? '99+' : totalItems}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </div>
                                <span className="hidden md:block font-semibold text-sm">
                                    {totalItems > 0 ? formatPrice(totalPrice) : 'Panier'}
                                </span>
                            </button>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="md:hidden p-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                                aria-label="Toggle menu"
                            >
                                {isMobileMenuOpen ? (
                                    <X className="w-5 h-5" />
                                ) : (
                                    <Menu className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Search - Expandable */}
                    <AnimatePresence>
                        {isSearchOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="md:hidden overflow-hidden"
                            >
                                <form onSubmit={handleSearch} className="pt-3">
                                    <div className="relative">
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Rechercher un produit..."
                                            className="w-full pl-12 pr-12 py-3 bg-gray-100 border-2 border-transparent rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-primary-500"
                                        />
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <button
                                            type="button"
                                            onClick={() => setIsSearchOpen(false)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:block border-t border-gray-100">
                    <div className="container-custom">
                        <div className="flex items-center gap-1 py-2">
                            <NavLink to="/" active={location.pathname === '/'} icon={Home}>
                                Accueil
                            </NavLink>
                            <NavLink to="/shop" active={location.pathname === '/shop' || location.pathname === '/products'} icon={Package}>
                                Boutique
                            </NavLink>
                            <div className="ml-auto flex items-center gap-4">
                                <span className="flex items-center gap-2 text-sm text-gray-600">
                                    <MapPin className="w-4 h-4 text-primary-500" />
                                    Livraison: 58 wilayas
                                </span>
                                <span className="flex items-center gap-2 text-sm text-green-600 font-medium">
                                    <Shield className="w-4 h-4" />
                                    Paiement sécurisé
                                </span>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                        <motion.nav
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 md:hidden shadow-2xl flex flex-col"
                        >
                            {/* Menu Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                <span className="font-bold text-lg text-gray-900">Menu</span>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Navigation Links */}
                            <div className="flex-1 overflow-y-auto py-4 px-3">
                                <div className="space-y-1">
                                    <MobileNavLink
                                        to="/"
                                        active={location.pathname === '/'}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        icon={Home}
                                    >
                                        Accueil
                                    </MobileNavLink>
                                    <MobileNavLink
                                        to="/shop"
                                        active={location.pathname === '/shop' || location.pathname === '/products'}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        icon={Package}
                                    >
                                        Boutique
                                    </MobileNavLink>
                                </div>

                                {/* Trust Badges in Mobile Menu */}
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
                                        Pourquoi nous choisir
                                    </p>
                                    <div className="space-y-3 px-3">
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                                                <Truck className="w-4 h-4 text-blue-500" />
                                            </div>
                                            <span>Livraison rapide - 58 wilayas</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                                                <Banknote className="w-4 h-4 text-green-500" />
                                            </div>
                                            <span>Paiement à la livraison</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                                                <Shield className="w-4 h-4 text-purple-500" />
                                            </div>
                                            <span>Produits garantis</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                                                <Star className="w-4 h-4 text-amber-500" />
                                            </div>
                                            <span>4.8/5 - 2000+ avis clients</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cart Preview in Mobile Menu */}
                            {items.length > 0 && (
                                <div className="p-4 border-t border-gray-100 bg-gray-50">
                                    <button
                                        onClick={() => {
                                            setIsMobileMenuOpen(false);
                                            openCart();
                                        }}
                                        className="w-full flex items-center justify-between p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <ShoppingCart className="w-5 h-5" />
                                            <span className="font-semibold">Voir le panier</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold">{formatPrice(totalPrice)}</span>
                                            <span className="bg-white/20 text-xs font-bold px-2 py-0.5 rounded-full">
                                                {totalItems}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </motion.nav>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

// Desktop nav link component
function NavLink({
    to,
    active,
    icon: Icon,
    children,
}: {
    to: string;
    active: boolean;
    icon: React.ElementType;
    children: React.ReactNode;
}) {
    return (
        <Link
            to={to}
            className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all',
                active 
                    ? 'bg-primary-50 text-primary-600' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            )}
        >
            <Icon className="w-4 h-4" />
            {children}
        </Link>
    );
}

// Mobile nav link component
function MobileNavLink({
    to,
    active,
    onClick,
    icon: Icon,
    children,
}: {
    to: string;
    active: boolean;
    onClick: () => void;
    icon: React.ElementType;
    children: React.ReactNode;
}) {
    return (
        <Link
            to={to}
            onClick={onClick}
            className={cn(
                'flex items-center justify-between py-3 px-4 rounded-xl font-medium transition-all',
                active
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
            )}
        >
            <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                <span>{children}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
        </Link>
    );
}
